/// IRPCS rule citations — the Part B (steering / sailing) and the
/// most-quoted Part D (signals) rules, structured for the Reference
/// look-up tab. Each rule has a one-line headline summary plus 2–5
/// key-point bullets that match the phrasing a sailor would memorise.
///
/// Source: COLREGS 1972 (as amended). Citations and wording match
/// the IMO consolidated edition.

export interface IrpcsRule {
  /// Rule number, e.g. "5", "13", "34".
  number: string;
  /// Short title from the regulation.
  title: string;
  /// One-line essence — surfaced at the top of the card.
  summary: string;
  /// Key sub-clauses or memorable phrases. Drives the bulleted list
  /// in the Reference view.
  keyPoints: readonly string[];
}

export const IRPCS_RULES: readonly IrpcsRule[] = Object.freeze([
  {
    number: "5",
    title: "Look-out",
    summary:
      "Maintain a proper look-out at all times — sight, hearing, and every available means.",
    keyPoints: [
      "Visual + aural + electronic (radar, AIS) — all means appropriate to the conditions.",
      "Goal: full appraisal of the situation and of the risk of collision.",
      "Applies in all conditions, day and night.",
    ],
  },
  {
    number: "6",
    title: "Safe speed",
    summary:
      "Proceed at a safe speed at all times so you can take proper avoiding action and stop in a safe distance.",
    keyPoints: [
      "Account for visibility, traffic density, manoeuvrability, sea state, and your draft.",
      "With radar, also consider radar limitations, scale, sea/weather clutter, and detection of small craft.",
      "Reduce speed in restricted visibility (linked to Rule 19).",
    ],
  },
  {
    number: "7",
    title: "Risk of collision",
    summary:
      "Use every appropriate means to determine if risk of collision exists. If in doubt, risk exists.",
    keyPoints: [
      "Radar — proper use including long-range scanning and plotting (or systematic observation).",
      "Compass bearings — if bearing of approaching vessel does not appreciably change, risk exists.",
      "Risk may exist even with appreciable bearing change — very large vessel or close range.",
    ],
  },
  {
    number: "8",
    title: "Action to avoid collision",
    summary:
      "Action shall be positive, made in ample time, and with due regard to good seamanship.",
    keyPoints: [
      "Alterations of course and/or speed shall be large enough to be readily apparent — small changes are forbidden.",
      "If sea room permits, course alteration alone is the most effective single action.",
      "An action that results in passing at a safe distance — and ensures effectiveness — must be checked until the other is finally past and clear.",
    ],
  },
  {
    number: "9",
    title: "Narrow channels",
    summary:
      "Keep to the outer starboard limit of a narrow channel as far as is safe and practicable.",
    keyPoints: [
      "Small vessels (<20 m) and sailing vessels shall not impede a vessel that can navigate safely only within the channel.",
      "Vessels engaged in fishing shall not impede the passage of any other vessel using the channel.",
      "Do not cross the channel if to do so impedes the passage of a vessel restricted to it.",
      "Overtaking only with agreement — sound signals Rule 34(c) — 2 long + 1 short to pass starboard, 2 long + 2 short to pass port.",
    ],
  },
  {
    number: "10",
    title: "Traffic separation schemes",
    summary:
      "Use lanes in the general direction of flow; cross at right angles if you must.",
    keyPoints: [
      "Avoid crossing traffic lanes — if obliged to cross, do so on a heading as nearly as practicable at right angles.",
      "Stay clear of the separation line or zone; join/leave at the termination or at the smallest practicable angle.",
      "Vessels under 20 m, sailing vessels, and fishing vessels shall not impede vessels following a traffic lane.",
    ],
  },
  {
    number: "12",
    title: "Sailing vessels",
    summary:
      "Port-tack gives way to starboard-tack; same tack, windward gives way.",
    keyPoints: [
      "Wind on different sides: vessel with wind on PORT side keeps out of the way.",
      "Wind on the same side: WINDWARD vessel keeps out of the way of LEEWARD.",
      "If on port tack and cannot determine the other's tack with certainty, assume she is starboard tack — keep clear.",
    ],
  },
  {
    number: "13",
    title: "Overtaking",
    summary:
      "Overtaking vessel keeps out of the way — overrides everything else in Section II.",
    keyPoints: [
      "Overtaking = approaching from more than 22.5° abaft the beam (you see only the sternlight).",
      "Sailing overtaking power still gives way — Rule 13 trumps Rule 18.",
      "Any subsequent change of bearing does not turn an overtaking situation into a crossing situation.",
    ],
  },
  {
    number: "14",
    title: "Head-on situation",
    summary:
      "Two power vessels meeting head-on — both alter to starboard.",
    keyPoints: [
      "Applies on reciprocal or nearly reciprocal courses with risk of collision.",
      "Each alters to starboard so each passes on the port side of the other.",
      "If in doubt whether head-on, assume it is and act accordingly.",
    ],
  },
  {
    number: "15",
    title: "Crossing situation",
    summary:
      "Two power vessels crossing — vessel with the other on her starboard side keeps out of the way.",
    keyPoints: [
      "Give-way vessel should, if possible, avoid crossing ahead of the stand-on vessel.",
      "Read with Rules 16 + 17 — Rule 15 says who gives way; 16/17 say what each does.",
    ],
  },
  {
    number: "16",
    title: "Action by give-way vessel",
    summary:
      "Give-way vessel takes early, substantial action to keep well clear.",
    keyPoints: [
      "Early — the action shall be taken when the situation first develops.",
      "Substantial — a small alteration is not enough; the stand-on vessel must be able to see what you're doing.",
    ],
  },
  {
    number: "17",
    title: "Action by stand-on vessel",
    summary:
      "Stand-on holds course and speed — until it's clear the give-way isn't acting; then she may, then must, take action.",
    keyPoints: [
      "(a)(i) Stand-on shall keep her course and speed.",
      "(a)(ii) May manoeuvre when it becomes apparent the give-way isn't taking appropriate action.",
      "(b) Shall take such action best aided to avoid collision when so close that give-way action alone cannot avoid it.",
      "(c) When in a crossing situation, must not alter course to port for a vessel on her port side.",
    ],
  },
  {
    number: "18",
    title: "Responsibilities between vessels",
    summary:
      "Priority hierarchy — each row gives way to all rows above it.",
    keyPoints: [
      "1. Not under command (NUC)",
      "2. Restricted in ability to manoeuvre (RAM)",
      "3. Constrained by her draft (in deep-draft channels)",
      "4. Engaged in fishing",
      "5. Sailing vessel",
      "6. Power-driven vessel",
      "Seaplanes and WIG craft keep well clear of all vessels and avoid impeding their navigation.",
      "Does not apply where Rules 9, 10, or 13 take precedence.",
    ],
  },
  {
    number: "19",
    title: "Conduct in restricted visibility",
    summary:
      "There is no stand-on vessel — both must take avoiding action. Safe speed throughout.",
    keyPoints: [
      "Proceed at a safe speed adapted to the prevailing visibility.",
      "(d) Radar-only detection: take avoiding action in ample time — avoid altering to port for a vessel forward of the beam (other than for one being overtaken), and avoid altering toward a vessel abeam or abaft the beam.",
      "(e) On hearing a fog signal apparently forward of the beam, reduce to bare steerage; if necessary stop and navigate with extreme caution.",
    ],
  },
  {
    number: "25",
    title: "Sailing vessels underway and vessels under oars",
    summary:
      "Lights + day shapes the sailing vessel must show. Defines what counts as a sailing vessel for the rule of the road.",
    keyPoints: [
      "Sidelights + sternlight at minimum — the same as a power-driven vessel, but no white masthead light (this is what makes you a 'sailing vessel' under the COLREGS).",
      "Optional: red over green all-round at the masthead. Easy to see, but conflicts with sidelights at close quarters — most yachts skip.",
      "Under 7 m: shall have ready at hand an electric torch or lighted lantern showing a white light, exhibited in sufficient time to prevent collision (Rule 25(d)(ii)).",
      "Under oars: may exhibit the sailing-vessel lights, but if not, must show a torch as above.",
      "DAY SHAPE — sailing under sail AND engine: a black cone, apex downwards, mounted forward (Rule 25(e)). Failing to show this when motor-sailing is the most-common COLREGS violation by yachts.",
      "When motor-sailing, you are a POWER-DRIVEN vessel for the rule of the road — give way as one (lose your stand-on privilege over other power vessels in crossing situations).",
    ],
  },
  {
    number: "32",
    title: "Sound signals — definitions",
    summary:
      "Definitions of 'whistle', 'short blast' and 'prolonged blast' that the rest of Part D uses.",
    keyPoints: [
      "'Whistle' = any sound signalling appliance capable of producing the prescribed blasts and complying with Annex III specifications.",
      "'Short blast' = about 1 second.",
      "'Prolonged blast' = 4 to 6 seconds.",
      "Distinction matters: 1 prolonged blast (power vessel making way in fog) is NOT the same as 1 short blast (altering course to starboard).",
    ],
  },
  {
    number: "33",
    title: "Equipment for sound signals",
    summary:
      "What vessels must carry to make the prescribed sound signals.",
    keyPoints: [
      "Vessels ≥ 12 m: whistle. Vessels ≥ 20 m: whistle AND bell. Vessels ≥ 100 m: whistle, bell AND gong (the gong sounds different so the listener can tell the forward + aft ends apart).",
      "Vessels < 12 m: not required to carry the prescribed equipment but MUST have some other means of making an efficient sound signal — an aerosol fog horn is the standard yacht option.",
      "Yachts: carry a portable aerosol horn + a spare canister. Practise the patterns — many sailors have never blown a fog signal in anger.",
    ],
  },
  {
    number: "34",
    title: "Manoeuvring and warning signals",
    summary:
      "Sound signals used when vessels are in sight of one another and taking action.",
    keyPoints: [
      "1 short blast (~1 s): 'I am altering my course to starboard'",
      "2 short blasts: 'I am altering my course to port'",
      "3 short blasts: 'I am operating astern propulsion'",
      "5 or more short rapid blasts (Rule 34(d)): doubt as to other's intentions or sufficient action",
      "Narrow channel overtaking — 2 long + 1 short = 'intend to overtake on your starboard side'; 2 long + 2 short = 'on your port side'; reply 1 long + 1 short + 1 long + 1 short = 'agree'",
      "1 prolonged blast (~5 s) when approaching a bend in a channel where vessels may be obscured.",
    ],
  },
  {
    number: "35",
    title: "Sound signals in restricted visibility",
    summary:
      "Fog signals broadcast every 1–2 minutes — distinct patterns identify the vessel's status.",
    keyPoints: [
      "Power-driven making way: 1 prolonged blast every 2 min.",
      "Power-driven under way but stopped: 2 prolonged blasts every 2 min.",
      "NUC, RAM, constrained-by-draft, sailing, fishing, towing: 1 prolonged + 2 short every 2 min.",
      "Vessel at anchor: rapid bell ringing for ~5 s every 1 min (gong aft as well if ≥ 100 m).",
      "Vessel aground: 3 distinct bell strokes + anchor signal + 3 distinct bell strokes, every 1 min.",
      "Pilot vessel on duty: 4 short blasts every 2 min, in addition to the power-driven signal.",
    ],
  },
  {
    number: "36",
    title: "Signals to attract attention",
    summary:
      "Light or sound signal a vessel may use to attract attention — provided it cannot be mistaken for any other signal authorised by the rules.",
    keyPoints: [
      "A vessel may make any light or sound signal that cannot be mistaken for an authorised distress, manoeuvring or warning signal.",
      "Common use: a powerful searchlight directed in the direction of the danger — provided it doesn't embarrass another vessel.",
      "Do NOT use white flares for this — they are reserved for collision avoidance specifically (see Annex IV signals).",
      "Direct the light signal in a way that does not blind the bridge of the vessel you're trying to alert.",
    ],
  },
  {
    number: "37",
    title: "Distress signals",
    summary:
      "Rule 37 + Annex IV — the recognised distress signals every vessel must understand.",
    keyPoints: [
      "Red flare (parachute rocket OR handheld) — the universal visual distress signal.",
      "Orange smoke — daytime equivalent.",
      "Continuous sounding with any fog-signalling apparatus.",
      "Gun or other explosive signal fired at intervals of ~1 minute.",
      "SOS (· · · — — — · · ·) sent by any signalling method (light, sound, radio).",
      "MAYDAY voice on VHF; DSC distress alert; EPIRB activation.",
      "Square flag with a ball above OR below it (the international flag + ball signal, rarely seen now but recognised).",
      "Slowly + repeatedly raising and lowering arms outstretched to each side.",
      "White flare = NOT a distress signal. White flares are collision-avoidance only (Annex IV).",
    ],
  },
]);
